import React, { createContext, useContext, ReactNode, useState, useCallback } from 'react';
import { authStorage } from '../services/api';

interface CenterConfig {
  id?: number;
  name: string;
  displayName: string;
  logo?: string;
  subdomain?: string;
  default_radiologist_id?: number | null;
}

interface CenterContextType {
  center: CenterConfig;
  refreshCenter: () => Promise<void>;
  isLoading: boolean;
}

const CenterContext = createContext<CenterContextType | undefined>(undefined);

export const useCenterConfig = () => {
  const context = useContext(CenterContext);
  if (!context) {
    throw new Error('useCenterConfig must be used within CenterProvider');
  }
  return context;
};

interface CenterProviderProps {
  children: ReactNode;
}

export const CenterProvider: React.FC<CenterProviderProps> = ({ children }) => {
  const [center, setCenter] = useState<CenterConfig>({
    name: 'Center Name',
    displayName: 'Center Name',
    subdomain: window.location.hostname.split('.')[0] || 'default',
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchCenterConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = authStorage().getItem('access_token');
      if (!token) {
        // No token — fetch center name via public endpoint using subdomain
        const subdomain = window.location.hostname.split('.')[0] || 'default';
        try {
          const publicRes = await fetch(`/api/v1/centers/public/info`);
          if (publicRes.ok) {
            const publicData = await publicRes.json();
            setCenter(prev => ({
              ...prev,
              name: publicData.name,
              displayName: publicData.name,
              subdomain,
            }));
          }
        } catch (e) {
          // ignore — keep default "Center Name"
        }
        setIsLoading(false);
        return;
      }
      
      // Get current user to find their center_id
      const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = await userResponse.json();
      
      // Fetch center details
      const centerResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/centers/${userData.center_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const centerData = await centerResponse.json();
      
      console.log('[CenterContext] Loaded center config:', {
        id: centerData.id,
        name: centerData.name,
        default_radiologist_id: centerData.default_radiologist_id
      });
      
      setCenter({
        id: centerData.id,
        name: centerData.name,
        displayName: centerData.name,
        subdomain: window.location.hostname.split('.')[0] || 'default',
        default_radiologist_id: centerData.default_radiologist_id
      });
    } catch (err) {
      console.error('Failed to fetch center config:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchCenterConfig();
  }, [fetchCenterConfig]);

  const value = {
    center,
    refreshCenter: fetchCenterConfig,
    isLoading,
  };

  return (
    <CenterContext.Provider value={value}>
      {children}
    </CenterContext.Provider>
  );
};
